import React, { useState, useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import ChatInput from 'app/components/ChatInput/index'
import Header from '../header'
import Messages from '../messages'
import EmojiPicker from 'app/components/ChatInput/emojiPicker'
import {
  sendMessage,
  sendNewTypedWord,
  setUserInput,
  setReplyingMessage,
  removeConversationNotSeenStart,
  setNotSeenReceivedMsgs,
} from 'app/redux/messages/messages.actions'
import { Close } from 'app/components/icons'
import { truncateText, convertHtmlToPlainText } from 'app/utils/text'
import LoadingMessagesFallback from '../components/LoadingMessages'
import { OutlinedWarning } from 'app/components/icons'
import './firstChat.scss'

const getUserInput = ({ messagesData }) => messagesData.messages.userInput
const getTexts = ({ widgetData }) => widgetData.widgetInfo.languageInfo.texts
const getActiveDepartment = ({ messagesData }) => messagesData.activeDepartment.data
const getDepartments = ({ messagesData }) => messagesData.departments.data
const getReplyingMessage = ({ messagesData }) => messagesData.replyingMessage
const getShowLauncher = ({ widgetData }) => widgetData.widgetInfo.showLauncher
const getAllDepartments = ({ messagesData }) => messagesData.departments.data
const getTeamsChatInputShouldHideIn = ({ messagesData }) => messagesData.teamsChatInputShouldHide
const getDisableInputStatus = ({ messagesData }) => messagesData.messages.disableInput?.status

const MAX_VALID_INPUT_CHARS = 1500

// FIXME: typo in file name
const FirstChat = () => {
  const [showIcons, setShowIcons] = useState(false)
  const [messages, setMessages] = useState([])

  const userInput = useSelector(getUserInput)
  const texts = useSelector(getTexts)
  const activeDepartment = useSelector(getActiveDepartment)
  const departments = useSelector(getDepartments)
  const replyingMessage = useSelector(getReplyingMessage)
  const showLauncher = useSelector(getShowLauncher)
  const allDepartments = useSelector(getAllDepartments)
  const teamsChatInputShouldHideIn = useSelector(getTeamsChatInputShouldHideIn)
  const disableInputStatus = useSelector(getDisableInputStatus)
  const getIsWidgetReady = ({ widgetData }) => widgetData.widgetInfo.isWidgetReady

  const dispatch = useDispatch()
  const textInput = React.createRef()

  const userInputValue = userInput[activeDepartment] || ''
  const textCharsLengthExceeded = userInputValue.length > MAX_VALID_INPUT_CHARS
  // hide if chat is not disabled due to form disable status
  const shouldHideChatFooter = teamsChatInputShouldHideIn.includes(activeDepartment) && !disableInputStatus
  // widget ready is set true when all messages are fetched and `flow` is done so it is used for showing messages fallback and chat input
  const isWidgetReady = useSelector(getIsWidgetReady)

  const toggleIcons = () => {
    setShowIcons(!showIcons)
  }

  const handleUserInput = (e) => {
    dispatch(setUserInput(e.target.value, activeDepartment))
  }

  const getTimeObject = () => {
    return new Date()
  }

  useEffect(() => {
    const getData = setTimeout(() => {
      // if (userInput) {
      dispatch(sendNewTypedWord(userInputValue))
      // }
    }, 500)
    return () => clearTimeout(getData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInput])

  // NOTE: we have exact function in greeting file. consider when refactoring
  const sendNewMessage = () => {
    if (userInputValue && userInputValue.trim() !== '') {
      const tmpId = Math.random().toString(36).substring(7)
      const newMessage = {
        content: userInputValue,
        me: true,
        type: 'text',
        status: 'pending',
        tmpId,
      }
      if (replyingMessage?.id) newMessage['replyId'] = replyingMessage?.id
      dispatch(
        sendMessage({
          message: { ...newMessage, createdAt: getTimeObject() },
          teamId: activeDepartment,
        }),
      )
      dispatch(setUserInput('', activeDepartment))
      textInput.current.focus()
    }
  }

  const handleSelectEmoji = (e) => {
    if (userInputValue && userInputValue !== '') {
      dispatch(setUserInput(userInputValue + e.target.alt, activeDepartment))
      textInput.current?.focus()
    } else {
      const tmpId = Math.random().toString(36).substring(7)
      const newEmojiMessage = {
        content: e.target.alt,
        me: true,
        type: 'emoji',
        status: 'pending',
        tmpId,
      }
      if (replyingMessage?.id) newEmojiMessage['replyId'] = replyingMessage?.id
      dispatch(
        sendMessage({
          message: { ...newEmojiMessage, createdAt: getTimeObject() },
          teamId: activeDepartment,
        }),
      )
      setShowIcons(false)
    }
  }

  const handleKeyDown = (e) => {
    if (!e || (e && e.key === 'Enter' && !e.shiftKey)) {
      sendNewMessage(e)
      e?.stopPropagation()
      e?.preventDefault()
      dispatch(setReplyingMessage(null))
    }
  }

  const handleClearReplyingMsg = () => {
    dispatch(setReplyingMessage(null))
  }

  const handleScrollIntoRepliedMsg = () => {
    const element = document.querySelector(`[data-id="${replyingMessage?.id}"]`)

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'end' })

      // add animation
      // element.classList.remove("animate_replied_msg");
      // void element.offsetWidth;
      // element.classList.add("animate_replied_msg");
    }
  }

  const getReplyingMsgContentForTextMsg = (content) => {
    if (replyingMessage.isAutoMessage) {
      return truncateText(convertHtmlToPlainText(content), 70)
    }

    return truncateText(content, 70)
  }

  const renderMessages = isWidgetReady ? <Messages messages={messages} /> : <LoadingMessagesFallback />
  const renderCharacterCountExceedWarningBar = (
    <div className="char_exceed_warning_bar">
      <div className="char_exceed_warning_bar__wrapper">
        <OutlinedWarning className="char_exceed_warning_bar__icon" />
        <div>{texts.checkLength?.replace('#count#', MAX_VALID_INPUT_CHARS)}</div>
      </div>
    </div>
  )

  useEffect(() => {
    let selectedDepartment = departments.filter((department) => department._id === activeDepartment)[0]
    setMessages(selectedDepartment?.messages || [])
  }, [activeDepartment, departments])

  // send a removeNotSeen req to the socket, if the user comes into the chat page.
  useEffect(() => {
    if (showLauncher) {
      let selectedDpData = {}
      // when there is only 1 department
      if (departments.length === 1) {
        const departmentId = departments[0]._id
        selectedDpData = allDepartments.find((item) => item._id === departmentId)
        // filter agent all messages
        const agentMessage = selectedDpData?.messages.find((msg) => !!msg.conversationId) || []

        const convId = selectedDpData?.messages[0]?.conversationId || agentMessage?.conversationId
        if (convId)
          dispatch(
            removeConversationNotSeenStart({
              conversationId: convId,
            }),
          )
        return
      }

      // when departments count is more than 1
      selectedDpData = departments.find((department) => department._id === activeDepartment)

      // filter agent all messages
      const agentMessage = selectedDpData?.messages?.find((msg) => !!msg.conversationId) || []

      /**
       * For sending a removeNotSeen req to the socket we need to know conversationsId,
       * When a chat is accepted newly, this conversationId only exist on agent messages,
       * Other than that this conversationId will be found on every message object that is received.
       */
      const convId = selectedDpData?.messages[0]?.conversationId || agentMessage?.conversationId

      if (convId) {
        dispatch(
          removeConversationNotSeenStart({
            conversationId: convId,
          }),
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLauncher])

  // clear notif count for a department if user visits it
  useEffect(() => {
    if (showLauncher && activeDepartment) {
      dispatch(setNotSeenReceivedMsgs({ [activeDepartment]: 0 }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLauncher, activeDepartment])

  return (
    <div className="messaging_page_wrapper">
      <div className="messaging_page">
        <Header />
        {renderMessages}
        {!shouldHideChatFooter && (
          <div className="messaging_page_footer">
            {!!replyingMessage && (
              <div className="replying_msg_preview">
                <div className="replying_msg_preview_body">
                  <span className="replying_msg_preview_body_tilte">{texts.reply}</span>
                  <span onClick={handleScrollIntoRepliedMsg} className="replying_msg_preview_body_text">
                    {replyingMessage?.msgType === 'text' || replyingMessage?.msgType === 'emoji'
                      ? getReplyingMsgContentForTextMsg(replyingMessage?.content)
                      : replyingMessage?.content}
                  </span>
                </div>

                <div onClick={handleClearReplyingMsg} className="replying_msg_preview_close">
                  <Close className="replying_msg_preview_close_icon" />
                </div>
              </div>
            )}

            {textCharsLengthExceeded && renderCharacterCountExceedWarningBar}

            <div className="messaging_page_chat_input_container">
              {isWidgetReady && (
                <ChatInput
                  disabled={textCharsLengthExceeded}
                  onChange={(e) => handleUserInput(e)}
                  onKeyDown={(e) => handleKeyDown(e)}
                  // value={userInput}
                  toggleIcons={toggleIcons}
                  ref={textInput}
                  {...{ showIcons }}
                />
              )}
            </div>
          </div>
        )}
        {showIcons && (
          <EmojiPicker
            onClick={(e) => {
              handleSelectEmoji(e)
            }}
          />
        )}
      </div>
    </div>
  )
}

export default React.memo(FirstChat)
